import React, { useState, useEffect } from 'react';
import api from './api';
import { getCommonEmailTypoSuggestion } from './emailTypo';
import { useToast } from './Toast';
import './Login.css';

function ResetPassword({ onSwitchToLogin }) {
  const { addToast } = useToast();
  const [email, setEmail] = useState('');
  const [emailTypoSuggestion, setEmailTypoSuggestion] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const applyEmailTypoSuggestion = () => {
    if (!emailTypoSuggestion) {
      return;
    }

    setEmail(emailTypoSuggestion.suggestedEmail);
    setEmailTypoSuggestion(null);
  };

  const handleSendCode = async ({ ignoreTypoWarning = false } = {}) => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      addToast('请输入邮箱地址！', 'error');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      addToast('请输入有效的邮箱地址！', 'error');
      return;
    }

    const suggestedEmail = getCommonEmailTypoSuggestion(trimmedEmail);
    if (suggestedEmail && !ignoreTypoWarning) {
      setEmailTypoSuggestion({ originalEmail: trimmedEmail, suggestedEmail });
      addToast('邮箱后缀可能输错了，请确认后再发送验证码。', 'error');
      return;
    }

    setEmailTypoSuggestion(null);
    setIsSendingCode(true);

    try {
      const response = await api.post('/api/send-verification-code', { email: trimmedEmail });
      addToast(response.data.message, 'success');
      setCountdown(60);
    } catch (error) {
      addToast(error.response?.data?.error || '发送验证码失败，请稍后重试！', 'error');
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!verificationCode) {
      addToast('请输入邮箱验证码！', 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      addToast('两次输入的密码不一致！', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await api.post('/api/reset-password', { email, verificationCode, newPassword });
      addToast(response.data.message, 'success');
      onSwitchToLogin();
    } catch (error) {
      addToast(error.response?.data?.error || '修改密码失败，请稍后重试！', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h2 className="login-title">修改密码</h2>
        <form className="login-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email" className="form-label">邮箱</label>
            <input
              type="email"
              id="email"
              className="form-input"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailTypoSuggestion(null);
              }}
              placeholder="请输入邮箱"
              required
            />
            {emailTypoSuggestion && (
              <div className="email-typo-warning">
                <div>
                  邮箱后缀可能输错了，是否想输入 {emailTypoSuggestion.suggestedEmail}？
                </div>
                <div className="email-typo-actions">
                  <button type="button" className="email-typo-button" onClick={applyEmailTypoSuggestion}>
                    改为建议邮箱
                  </button>
                  <button
                    type="button"
                    className="email-typo-button"
                    disabled={isSendingCode}
                    onClick={() => handleSendCode({ ignoreTypoWarning: true })}
                  >
                    仍然发送
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="verificationCode" className="form-label">验证码</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="text"
                id="verificationCode"
                className="form-input"
                style={{ flex: 1 }}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="请输入验证码"
                required
              />
              <button
                type="button"
                style={{
                  padding: '0 16px',
                  backgroundColor: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: countdown > 0 ? 'not-allowed' : 'pointer',
                  opacity: countdown > 0 ? 0.6 : 1
                }}
                disabled={countdown > 0 || isSendingCode}
                onClick={handleSendCode}
              >
                {isSendingCode ? '发送中...' : countdown > 0 ? `${countdown}s后重发` : '发送验证码'}
              </button>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="newPassword" className="form-label">新密码</label>
            <input
              type="password"
              id="newPassword"
              className="form-input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="请输入新密码"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">确认新密码</label>
            <input
              type="password"
              id="confirmPassword"
              className="form-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="请再次输入新密码"
              required
            />
          </div>
          <button
            type="submit"
            className="login-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? '修改中...' : '修改密码'}
          </button>
        </form>
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <span style={{ fontSize: '14px', color: '#666' }}>返回登录？</span>
          <button
            style={{
              background: 'none',
              border: 'none',
              color: '#667eea',
              cursor: 'pointer',
              fontSize: '14px',
              padding: '0',
              textDecoration: 'underline',
              marginLeft: '5px'
            }}
            onClick={onSwitchToLogin}
          >
            立即登录
          </button>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
